import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../../Services/auth.service';
import { AuthTokenService } from '../../../Services/auth-token.service';
import { AuthResponse, LaravelDefaultResponse } from '../../../Interfaces/response.interface';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../../../environments/endpoints';

let refreshRequest$: Observable<AuthResponse> | null = null;

function isRefreshRejected(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    (error.status === 401 || error.status === 403 || error.status === 422)
  );
}

function withAccessToken(req: HttpRequest<unknown>, access_token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${access_token}`,
    },
  });
}

function redirectToLogin(
  authTokenService: AuthTokenService,
  authService: AuthService,
  router: Router,
): void {
  authTokenService.setReturnUrl(router.url);
  // Not clearToken(): it leaves isAuthenticatedSubject true, which authGuard trusts.
  authService.clearAuth();
  router.navigate(['/login']);
}

function refreshAccessToken(
  http: HttpClient,
  authTokenService: AuthTokenService,
): Observable<AuthResponse> {
  if (!refreshRequest$) {
    const refresh_token = authTokenService.getRefreshToken();

    if (!refresh_token) {
      return throwError(() => new Error('No refresh token available.'));
    }

    refreshRequest$ = http
      .post<LaravelDefaultResponse<AuthResponse>>(
        `${environment.api.host}/${endpoints.auth.refresh}`,
        { refresh_token },
        {
          headers: {
            'skip-loading': 'true',
          },
        },
      )
      .pipe(
        map((response) => response.data),
        tap((auth_data) => authTokenService.setToken(auth_data)),
        finalize(() => {
          refreshRequest$ = null;
        }),
        shareReplay(1),
      );
  }

  return refreshRequest$;
}

// No pre-flight refresh: parallel guards would reuse the token the boot refresh is rotating.
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const httpBackend = inject(HttpBackend);
  const authTokenService = inject(AuthTokenService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const http = new HttpClient(httpBackend);

  const handleUnauthorized = (
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> => {
    const is_unauthorized = error.status === 401;
    const is_refresh_call = request.url.includes(endpoints.auth.refresh);
    const is_logout_call = request.url.includes(endpoints.auth.logout);
    const has_refresh_token = authTokenService.hasRefreshToken();

    if (!is_unauthorized || is_logout_call) {
      return throwError(() => error);
    }

    if (is_refresh_call || !has_refresh_token) {
      redirectToLogin(authTokenService, authService, router);
      return throwError(() => error);
    }

    return refreshAccessToken(http, authTokenService).pipe(
      catchError((refresh_error: unknown) => {
        if (isRefreshRejected(refresh_error)) {
          redirectToLogin(authTokenService, authService, router);
        }

        return throwError(() => refresh_error);
      }),
      switchMap((auth_data) => next(withAccessToken(request, auth_data.access_token))),
    );
  };

  const access_token = authTokenService.getToken();
  const request =
    access_token && !authTokenService.isTokenExpired() ? withAccessToken(req, access_token) : req;

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => handleUnauthorized(request, error)),
  );
};
